import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { ActivatedRoute } from '@angular/router';

import { forkJoin, take } from 'rxjs';

import { ToastrService } from 'ngx-toastr';

import { Course, Hole, Round, HoleYardage } from '../../../models';
import { CourseService, TournamentService } from '../../../services';
import { LoggingService } from '../../../services/shared/logging/logging.service';

import { HoleInfoComponent } from '../../holes/hole-info/hole-info.component';
import { AuditInfoComponent } from '../../shared/audit-info/audit-info.component';
import { resolveTournamentLpgaId } from '../../../utils/tournament.utils';

type CourseSnapshot = { name?: string; shortName?: string; lpgaId?: number };

@Component({
  standalone: true,
  imports: [FormsModule, HoleInfoComponent, AuditInfoComponent],
  selector: 'app-course-detail',
  templateUrl: './course-detail.component.html',
  styleUrls: ['./course-detail.component.scss'],
})
export class CourseDetailComponent implements OnInit {
  private loggingService = inject(LoggingService);

  private holesToSave: Hole[] = [];
  private holeYardagesToSave: HoleYardage[] = [];
  private courseBaseline?: CourseSnapshot;

  private pendingSaveOperations = 0;
  private hasSaveSuccess = false;
  private hasSaveError = false;

  tournamentId?: string;
  courseId?: string;
  course?: Course;
  isCourseChanged = false;

  rounds: Round[] = [];
  holes: Hole[] = [];
  holeYardages: HoleYardage[] = [];

  constructor(
    private readonly courseService: CourseService,
    private readonly tournamentService: TournamentService,
    private readonly toastrService: ToastrService,
    private readonly route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.tournamentId = params['tournamentId'];
      if (this.tournamentId) {
        this.loadTournamentRounds();
      }
    });

    this.route.data.subscribe(({ course }) => {
      if (!course) {
        return;
      }

      this.course = course;
      this.courseId = course.courseId;
      this.captureCourseBaseline();
      this.ensureCourseLpgaId();
      this.loadCourseAssets();
    });
  }

  private loadCourseAssets(): void {
    if (!this.courseId) {
      return;
    }

    this.holes = [];
    this.holeYardages = [];
    this.holesToSave = [];
    this.holeYardagesToSave = [];

    forkJoin({
      holes: this.courseService.getHolesByCourse(this.courseId),
      holeYardages: this.courseService.getHoleYardagesByCourse(this.courseId),
    }).subscribe({
      next: ({ holes, holeYardages }) => {
        this.holes = holes;
        this.holeYardages = holeYardages;
      },
      error: (err) => {
        this.toastrService.error(
          'Failed to load Course assets. Please try again.',
          'Error'
        );
        this.loggingService.error('Error loading course assets:', err);
      },
    });
  }

  private loadTournamentRounds(): void {
    if (!this.tournamentId) {
      return;
    }

    this.tournamentService.getRounds(this.tournamentId).subscribe({
      next: (rounds) => {
        this.rounds = rounds;
      },
      error: (err) => {
        this.toastrService.error(
          'Failed to load Rounds. Please try again.',
          'Error'
        );
        this.loggingService.error('Error loading rounds:', err);
      },
    });
  }

  onHoleChange(hole: Hole): void {
    const existingHoleIndex = this.holesToSave.findIndex(
      (h) => h.holeId === hole.holeId
    );
    if (existingHoleIndex !== -1) {
      this.holesToSave[existingHoleIndex] = hole;
    } else {
      this.holesToSave.push(hole);
    }
  }

  onHoleYardageChange(holeYardage: HoleYardage): void {
    const existingYardageIndex = this.holeYardagesToSave.findIndex(
      (h) => h.holeYardageId === holeYardage.holeYardageId
    );
    if (existingYardageIndex !== -1) {
      this.holeYardagesToSave[existingYardageIndex] = holeYardage;
    } else {
      this.holeYardagesToSave.push(holeYardage);
    }
  }

  save(): void {
    if (!this.canSave) {
      this.toastrService.info('No changes to save.', 'Info');
      return;
    }

    if (this.isCourseChanged) {
      this.saveCourse();
    }

    this.saveHoles();
    this.saveHoleYardages();
  }

  private saveCourse(): void {
    if (!this.course || !this.isCourseChanged) {
      return;
    }

    const lpgaIdValue = Number(this.course.lpgaId);
    if (!Number.isFinite(lpgaIdValue) || lpgaIdValue <= 0) {
      this.toastrService.error(
        'LPGA ID is required to save this course.',
        'Validation'
      );
      return;
    }

    this.course.lpgaId = lpgaIdValue;
    this.beginSaveOperation();

    this.courseService.updateCourse(this.course).subscribe({
      next: (updatedCourse) => {
        this.course = { ...updatedCourse } as Course;
        this.captureCourseBaseline();
        this.completeSaveOperation(true);
      },
      error: (err) => {
        this.toastrService.error(
          'Failed to save Course. Please try again.',
          'Failure'
        );
        this.loggingService.error('Error saving course:', err);
        this.completeSaveOperation(false);
      },
    });
  }

  private saveHoles(): void {
    if (this.holesToSave.length === 0) {
      return;
    }

    this.beginSaveOperation();

    forkJoin(
      this.holesToSave.map((hole) => this.courseService.saveHole(hole))
    ).subscribe({
      next: (savedHoles) => {
        this.holes = this.holes.map(
          (hole) =>
            savedHoles.find((savedHole) => savedHole.holeId === hole.holeId) ||
            hole
        );
        this.holesToSave = [];
        this.completeSaveOperation(true);
      },
      error: (err) => {
        this.toastrService.error(
          'Failed to save Holes. Please try again.',
          'Failure'
        );
        this.loggingService.error('Error saving Holes:', err);
        this.completeSaveOperation(false);
      },
    });
  }

  private saveHoleYardages(): void {
    if (this.holeYardagesToSave.length === 0) {
      return;
    }

    this.beginSaveOperation();

    forkJoin(
      this.holeYardagesToSave.map((holeYardage) =>
        this.courseService.saveHoleYardage(holeYardage)
      )
    ).subscribe({
      next: (savedYardages) => {
        this.holeYardages = this.holeYardages.map(
          (yardage) =>
            savedYardages.find(
              (savedYardage) =>
                savedYardage.holeYardageId === yardage.holeYardageId
            ) || yardage
        );
        this.holeYardagesToSave = [];
        this.completeSaveOperation(true);
      },
      error: (err) => {
        this.toastrService.error(
          'Failed to save Hole Yardages. Please try again.',
          'Failure'
        );
        this.loggingService.error('Error saving Hole Yardages:', err);
        this.completeSaveOperation(false);
      },
    });
  }

  private beginSaveOperation(): void {
    if (this.pendingSaveOperations === 0) {
      this.hasSaveSuccess = false;
      this.hasSaveError = false;
    }
    this.pendingSaveOperations += 1;
  }

  private completeSaveOperation(succeeded: boolean): void {
    if (succeeded) {
      this.hasSaveSuccess = true;
    } else {
      this.hasSaveError = true;
    }

    this.pendingSaveOperations = Math.max(0, this.pendingSaveOperations - 1);

    if (
      this.pendingSaveOperations === 0 &&
      this.hasSaveSuccess &&
      !this.hasSaveError
    ) {
      this.toastrService.success('Course saved', 'Success');
    }
  }

  private ensureCourseLpgaId(): void {
    if (!this.course) {
      return;
    }

    const currentValue = Number(this.course.lpgaId);
    if (Number.isFinite(currentValue) && currentValue > 0) {
      return;
    }

    const referenceTournamentId = this.course.tournamentId || this.tournamentId;
    if (!referenceTournamentId) {
      return;
    }

    this.tournamentService
      .getById(referenceTournamentId)
      .pipe(take(1))
      .subscribe({
        next: (tournament) => {
          const resolved = resolveTournamentLpgaId(tournament);
          if (resolved && this.course) {
            this.course.lpgaId = resolved;
            this.captureCourseBaseline();
          }
        },
        error: (error) => {
          this.loggingService.error('Error resolving tournament LPGA ID', error);
        },
      });
  }

  onCourseFieldChanged(): void {
    this.evaluateCourseChanges();
  }

  onCourseLpgaIdChanged(value: string | number | null): void {
    if (!this.course) {
      return;
    }

    const normalized = this.normalizeLpgaId(value);
    this.course.lpgaId = normalized;
    this.evaluateCourseChanges();
  }

  get canSave(): boolean {
    return (
      this.isCourseChanged ||
      this.holesToSave.length > 0 ||
      this.holeYardagesToSave.length > 0
    );
  }

  private captureCourseBaseline(): void {
    if (!this.course) {
      this.courseBaseline = undefined;
      this.isCourseChanged = false;
      return;
    }

    this.courseBaseline = {
      name: this.course.name ?? '',
      shortName: this.course.shortName ?? '',
      lpgaId: this.normalizeLpgaId(this.course.lpgaId),
    };
    this.isCourseChanged = false;
  }

  private evaluateCourseChanges(): void {
    if (!this.course) {
      this.isCourseChanged = false;
      return;
    }

    if (!this.courseBaseline) {
      this.isCourseChanged = true;
      return;
    }

    const normalizedLpga = this.normalizeLpgaId(this.course.lpgaId);
    this.isCourseChanged =
      (this.course.name ?? '') !== (this.courseBaseline.name ?? '') ||
      (this.course.shortName ?? '') !== (this.courseBaseline.shortName ?? '') ||
      normalizedLpga !== this.normalizeLpgaId(this.courseBaseline.lpgaId);
  }

  private normalizeLpgaId(value: unknown): number | undefined {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : undefined;
  }
}
